import React, { useState } from 'react';
import {
  DotsSixVertical,
  CaretDown,
  Drop,
  Eyedropper,
  Flask,
  ShoppingBag,
  Sun,
  Sparkle,
  Icon,
} from 'phosphor-react';
import LazyImage from '../ui/LazyImage';
import usePresence from '../../use/usePresence';
import ProductDrawer from '../routine/ProductDrawer';
import { CatalogProduct } from '../../types/entities';

// ponytail: mapa ingênuo nome→ícone (mock 2026-06-11); refinar quando categorias tiverem ícone próprio no backend.
const CATEGORY_ICONS: [RegExp, Icon][] = [
  [/limpe|gel|sabonete|cleanser/i, Drop],
  [/s[ée]rum/i, Eyedropper],
  [/t[ôo]nico|esfoliante|[áa]cido/i, Flask],
  [/protetor|solar|spf|fps/i, Sun],
];

function categoryIcon(name: string): Icon {
  return CATEGORY_ICONS.find(([re]) => re.test(name))?.[1] ?? Sparkle;
}

interface StepCardProps {
  stepOrder: number;
  /** Total de etapas do período: liga o rótulo "Etapa X de Y". */
  totalSteps?: number;
  productCategoryName: string;
  instructions?: string;
  /** Categoria da etapa — sem ela não há o que oferecer na gaveta. */
  productCategoryId?: string;
  /** Produto já escolhido para a etapa. */
  productId?: string | null;
  userProductId?: string | null;
  productName?: string | null;
  productImageUrl?: string | null;
  /**
   * Presente = o cartão vira "escolher produto": o botão de descrição abre a gaveta
   * com os produtos da categoria. Ausente (dashboard) = só expande as instruções.
   */
  onSelectProduct?: (product: CatalogProduct | null) => void;
  onEdit?: () => void;
  onRemove?: () => void;
  dragHandleProps?: Record<string, unknown>;
  isDragging?: boolean;
  className?: string;
}

export default function StepCard({
  stepOrder,
  totalSteps,
  productCategoryName,
  instructions,
  productCategoryId,
  productId,
  userProductId,
  productName,
  productImageUrl,
  onSelectProduct,
  onEdit,
  onRemove,
  dragHandleProps = {},
  isDragging = false,
  className = '',
}: StepCardProps) {
  const [expanded, setExpanded] = useState(false);
  const revelado = usePresence(expanded, 180);
  const draggable = Object.keys(dragHandleProps).length > 0;
  const Icon = categoryIcon(productCategoryName);
  const escolheProduto = Boolean(onSelectProduct && productCategoryId);
  const abrivel = escolheProduto || Boolean(instructions);

  return (
    <div
      className={`
        flex flex-col
        p-4
        bg-white dark:bg-gray-800
        rounded-card
        shadow-soft dark:shadow-none
        border border-nude-100 dark:border-gray-700
        transition-[border-color,box-shadow] duration-200 ease-soft
        hover:shadow-soft-md
        group
        ${isDragging ? 'shadow-lg ring-2 ring-blush-200 opacity-90' : ''}
        ${className}
      `}
    >
      <div className="flex items-center gap-3">
        {/* Drag handle (só no editor) */}
        {draggable && (
          <div
            className="flex-shrink-0 text-nude-300 cursor-grab active:cursor-grabbing touch-none"
            {...(dragHandleProps as React.HTMLAttributes<HTMLDivElement>)}
          >
            <DotsSixVertical className="w-4 h-4" />
          </div>
        )}

        {/* Step Number */}
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blush-100 dark:bg-blush-900/40 text-blush-600 dark:text-blush-300 text-xs font-bold">
          {stepOrder}
        </div>

        {/* Ícone da categoria (mock 2026-06-11) */}
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-nude-50 dark:bg-gray-700/60">
          <Icon className="w-5 h-5 text-blush-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {totalSteps != null && (
            <p className="text-[10px] font-semibold uppercase tracking-wider text-nude-400 dark:text-gray-500">
              Etapa {stepOrder} de {totalSteps}
            </p>
          )}
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">
            {productCategoryName}
          </h4>
          {productName ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-blush-500 dark:text-blush-400 font-medium truncate">
              <ShoppingBag className="w-3 h-3 flex-shrink-0" />
              {productName}
            </p>
          ) : (
            instructions && !expanded && (
              <p className="mt-0.5 text-xs text-nude-500 dark:text-gray-400 truncate">
                {instructions}
              </p>
            )
          )}
        </div>

        {/* Miniatura do produto escolhido */}
        {productImageUrl && (
          <LazyImage
            src={productImageUrl}
            alt={productName ?? ''}
            className="flex-shrink-0 w-9 h-9 rounded-lg bg-nude-50 dark:bg-gray-700"
            imgClassName="w-full h-full object-contain"
          />
        )}

        {/* Expand toggle: com onSelectProduct é ele que abre a gaveta de produtos */}
        {abrivel && (
          <button
            onClick={() => setExpanded((prev) => !prev)}
            className="flex-shrink-0 p-1.5 text-nude-400 hover:text-blush-500 hover:bg-blush-50 rounded-lg transition-colors active:scale-[0.97]"
            aria-label={
              escolheProduto
                ? (expanded ? 'Fechar produtos da etapa' : 'Ver produtos da etapa')
                : (expanded ? 'Recolher instruções' : 'Expandir instruções')
            }
          >
            <CaretDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {onEdit && (
            <button
              onClick={onEdit}
              className="p-1.5 text-nude-400 hover:text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
              aria-label="Editar etapa"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
          {onRemove && (
            <button
              onClick={onRemove}
              className="p-1.5 text-nude-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              aria-label="Remover etapa"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {/*
        O conteúdo revelado entra com opacidade + deslocamento; a altura não é
        animada de propósito (é propriedade de layout e faz reflow por frame).
      */}
      {instructions && revelado.montado && (
        <p
          data-state={revelado.estado}
          className="origin-top mt-2 text-xs text-nude-500 dark:text-gray-400 leading-relaxed data-[state=open]:animate-enter data-[state=closed]:animate-exit"
        >
          {instructions}
        </p>
      )}

      {escolheProduto && expanded && (
        <ProductDrawer
          categoryId={productCategoryId!}
          stepLabel={totalSteps != null ? `Etapa ${stepOrder} de ${totalSteps} · ${productCategoryName}` : undefined}
          selectedProductId={productId}
          selectedUserProductId={userProductId}
          onSelect={onSelectProduct!}
        />
      )}
    </div>
  );
}
